import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Handles the return leg of Emergent-managed Google sign-in.
// Rendered by AppRoutes whenever the URL fragment contains `session_id=`.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const { setSessionFromToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const hash = window.location.hash || "";
      const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      // Clear the fragment immediately so refreshes don't re-process it.
      window.history.replaceState(null, "", window.location.pathname);
      if (!sessionId) {
        navigate("/auth/login", { replace: true });
        return;
      }
      try {
        const r = await api.post("/auth/google/session", {
          session_id: sessionId,
          ref_code: localStorage.getItem("fp_ref_code") || null,
        });
        const { access_token, user } = r.data;
        await setSessionFromToken(access_token, user);
        if (user.is_coach) navigate("/coach-dashboard", { replace: true });
        else if (!user.onboarded) navigate("/onboarding", { replace: true });
        else navigate("/dashboard", { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Google sign-in failed — please try again");
        navigate("/auth/login", { replace: true });
      }
    };
    run();
  }, [navigate, setSessionFromToken]);

  // Silent processing — minimal dark screen, no flashing UI.
  return (
    <div className="min-h-screen bg-obsidian-950 flex items-center justify-center" data-testid="google-auth-callback">
      <div className="overline text-zinc-500">Signing you in…</div>
    </div>
  );
}
